"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { fadeInUp, staggerContainer } from "@/lib/animations";
import GlassCard from "@/components/ui/GlassCard";
import Button from "@/components/ui/Button";
import {
  Calendar,
  MessageCircle,
  CheckCircle,
  CheckCircle2,
  XCircle,
} from "lucide-react";

export default function CTA() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    const form = e.currentTarget;

    try {
      const response = await fetch("/api/cta", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: "idworks Hub Visit Request",
          message: formData.message,
        }),
      });

      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);

      if (response.status === 200) {
        console.log("Setting success status");
        setSubmitStatus("success");
        setFormData({ name: "", email: "", phone: "", message: "" });
        form.reset();

        // Auto-hide success message after 5 seconds
        setTimeout(() => setSubmitStatus("idle"), 5000);
      } else {
        console.log("Setting error status");
        setSubmitStatus("error");
        setTimeout(() => setSubmitStatus("idle"), 5000);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setSubmitStatus("error");
      setTimeout(() => setSubmitStatus("idle"), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const whatsappNumber = "+966540414979";

  return (
    <section id="cta" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-7xl">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Section Header */}
          <motion.div
            variants={fadeInUp}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
              Become an ID Worker
            </h2>
            <p className="text-text text-base sm:text-lg md:text-xl max-w-3xl mx-auto px-4">
              Join our creative community and unlock your full potential. Get
              started today!
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Contact Form */}
            <GlassCard className="p-6 sm:p-8 lg:p-10">
              <h3 className="text-heading text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
                Schedule a Workspace Visit
              </h3>
              <p className="text-text text-sm sm:text-base mb-6 sm:mb-8">
                Fill out the form below and we'll get back to you within 24
                hours to schedule your visit.
              </p>

              {/* Success Message */}
              {submitStatus === "success" && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/20 border border-green-500/50 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <p className="text-sm text-green-100">
                    Thank you! We've received your request and will contact you
                    within 24 hours to schedule your visit.
                  </p>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === "error" && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/20 border border-red-500/50 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-100">
                    Oops! Something went wrong. Please try again or contact us
                    via WhatsApp.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                <div className="w-full">
                  <label
                    htmlFor="name"
                    className="block text-heading font-semibold mb-2 text-sm sm:text-base"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 sm:py-3.5 rounded-xl bg-card border-2 border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all text-heading placeholder:text-muted-foreground text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="John Doe"
                  />
                </div>

                <div className="w-full">
                  <label
                    htmlFor="email"
                    className="block text-heading font-semibold mb-2 text-sm sm:text-base"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 sm:py-3.5 rounded-xl bg-card border-2 border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all text-heading placeholder:text-muted-foreground text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="john@example.com"
                  />
                </div>

                <div className="w-full">
                  <label
                    htmlFor="phone"
                    className="block text-heading font-semibold mb-2 text-sm sm:text-base"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 sm:py-3.5 rounded-xl bg-card border-2 border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all text-heading placeholder:text-muted-foreground text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="+966 54 041 4979"
                  />
                </div>

                <div className="w-full">
                  <label
                    htmlFor="message"
                    className="block text-heading font-semibold mb-2 text-sm sm:text-base"
                  >
                    Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 sm:py-3.5 rounded-xl bg-card border-2 border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-none text-heading placeholder:text-muted-foreground text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Tell us about your needs..."
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  size="lg"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                      Schedule Visit
                    </>
                  )}
                </Button>
              </form>
            </GlassCard>

            {/* Quick Actions */}
            <div className="space-y-6 sm:space-y-8">
              {/* WhatsApp Card */}
              <GlassCard className="p-6 sm:p-8 lg:p-10">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8 text-green-500" />
                  </div>
                  <div className="flex-grow w-full sm:w-auto">
                    <h3 className="text-heading text-lg sm:text-xl font-bold mb-2 sm:mb-3">
                      Chat on WhatsApp
                    </h3>
                    <p className="text-text text-sm sm:text-base mb-5 sm:mb-6">
                      Have questions? Get instant answers from our team via
                      WhatsApp.
                    </p>
                    <a
                      href={`https://wa.me/${whatsappNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full"
                    >
                      <Button variant="outline" className="w-full">
                        <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                        Open WhatsApp
                      </Button>
                    </a>
                  </div>
                </div>
              </GlassCard>

              {/* Benefits Card */}
              <GlassCard className="p-6 sm:p-8 lg:p-10 bg-gradient-to-br from-primary/10 to-transparent">
                <h3 className="text-heading text-lg sm:text-xl lg:text-2xl font-bold mb-5 sm:mb-6">
                  Why Join idworks Hub?
                </h3>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    "Access to premium workspace facilities",
                    "Vibrant creative community",
                    "Regular networking events",
                    "Professional development opportunities",
                    "Central location in KAFD, Riyadh",
                  ].map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-text text-sm sm:text-base leading-relaxed">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
              </GlassCard>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
